import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import { External } from "components/icons/generated";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageUserGuides } from "hooks/useAnalytics/pages/userGuides";
import { AssistantPanelMode } from "components/AssistantPanel/Layout/types";
import { ASSISTANT_LABELS } from "components/AssistantPanel/Layout/Header/constants";

import styles from "./styles.module.css";
import useAssistantPanelStore from "../Layout/useStore";
import useAssistantPanelLearnStore from "./useStore";
import AssistantPanelLayoutHeader from "../Layout/Header";
import AssistantPanelLearnChaptersView from "./components/ChaptersView";
import AssistantPanelLearnGroupsView from "./components/GroupsView";
import AssistantPanelLearnActiveGuideView from "./components/ActiveGuideView";
import useAssistantPanelLearnActiveGuide from "./useActiveGuide";
import AssistantPanelLearnNavigationBackButton from "./components/NavigationBackButton";

const AssistantPanelLearn = () => {
  const navigate = useNavigate();
  const { setIsOpen } = useAssistantPanelStore();
  const { navigationState } = useAssistantPanelLearnStore();
  const { activeGuide, currentStep } = useAssistantPanelLearnActiveGuide();

  const trackEvent = useAnalytics({ page: AnalyticsPageUserGuides.UserGuides });

  const userGuideName = activeGuide?.metadata.title;
  const userGuideStep = currentStep?.title;

  const handleModeChange = useCallback(
    (mode: AssistantPanelMode) => {
      trackEvent("Go To Button Clicked", {
        button: ASSISTANT_LABELS[mode],
        userGuideName,
        userGuideStep,
      });
    },
    [trackEvent, userGuideName, userGuideStep]
  );

  const handlePinChange = useCallback(
    (isPinned: boolean) => {
      trackEvent("Pin Updated", {
        status: isPinned ? "pinned" : "unpinned",
        userGuideName,
        userGuideStep,
      });
    },
    [trackEvent, userGuideName, userGuideStep]
  );

  const handleGoToDashboard = useCallback(() => {
    setIsOpen(false);
    navigate("/launchpad");
  }, [navigate, setIsOpen]);

  return (
    <>
      <AssistantPanelLayoutHeader onModeChange={handleModeChange} onPinChange={handlePinChange}>
        <AssistantPanelLearnNavigationBackButton />
      </AssistantPanelLayoutHeader>

      {navigationState.level === "groups" && <AssistantPanelLearnGroupsView />}
      {navigationState.level === "chapters" && <AssistantPanelLearnChaptersView />}
      {navigationState.level === "guide" && <AssistantPanelLearnActiveGuideView />}

      <Box direction="column" padding="x-large">
        <Box
          align="center"
          justify="center"
          gap="medium"
          padding="x-large 0 0 0"
          className={styles.dashboardLink}
        >
          <Button variant="ghost" size="small" endIcon={External} onPress={handleGoToDashboard}>
            View full learning dashboard
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AssistantPanelLearn;
